import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, getDaysInMonth, startOfMonth, getDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const months = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function DatePicker({ value, onChange, placeholder = "Selecione uma data", className, disabled }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    if (value) {
      return new Date(value + 'T00:00:00');
    }
    return new Date();
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      return new Date(value + 'T00:00:00');
    }
    return null;
  });
  const [yearInput, setYearInput] = useState(currentDate.getFullYear().toString());
  const [showYearInput, setShowYearInput] = useState(false);
  const yearInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) {
      const date = new Date(value + 'T00:00:00');
      setSelectedDate(date);
      setCurrentDate(date);
      setYearInput(date.getFullYear().toString());
    }
  }, [value]);

  useEffect(() => {
    if (showYearInput && yearInputRef.current) {
      yearInputRef.current.focus();
      yearInputRef.current.select();
    }
  }, [showYearInput]);

  const handleDateSelect = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newDate);
    const formattedDate = format(newDate, 'yyyy-MM-dd');
    onChange(formattedDate);
    setIsOpen(false);
  };

  const handleMonthChange = (monthIndex: string) => {
    const newDate = new Date(currentDate.getFullYear(), parseInt(monthIndex), 1);
    setCurrentDate(newDate);
  };

  const handleYearSubmit = () => {
    const year = parseInt(yearInput);
    if (year >= 1900 && year <= new Date().getFullYear() + 10) {
      const newDate = new Date(year, currentDate.getMonth(), 1);
      setCurrentDate(newDate);
    }
    setShowYearInput(false);
  };

  const handleYearInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleYearSubmit();
    } else if (e.key === 'Escape') {
      setYearInput(currentDate.getFullYear().toString());
      setShowYearInput(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getDay(startOfMonth(currentDate));
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentDate.getMonth() && 
        selectedDate.getFullYear() === currentDate.getFullYear();
      
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <Button
          key={day}
          variant={isSelected ? "default" : "ghost"}
          size="sm"
          className={cn(
            "p-2 h-8 w-8 text-sm font-normal rounded-full border transition-all",
            isSelected ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent hover:border-accent",
            isToday && !isSelected && "bg-accent border-accent"
          )}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </Button>
      );
    }

    return days;
  };

  const displayValue = selectedDate ? format(selectedDate, "dd/MM/yyyy", { locale: ptBR }) : "";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "text-base font-semibold text-center min-w-[160px] px-4 py-2 rounded-full border border-border hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all cursor-pointer",
            !selectedDate && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
        >
          {displayValue || placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          {/* Header with navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth('prev')}
              className="h-10 w-10 p-0 rounded-full border border-border hover:bg-accent hover:border-accent"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Select value={currentDate.getMonth().toString()} onValueChange={handleMonthChange}>
                <SelectTrigger className="w-auto h-7 text-sm rounded-full border border-border hover:bg-accent hover:border-accent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {showYearInput ? (
                <Input
                  ref={yearInputRef}
                  type="number"
                  value={yearInput}
                  onChange={(e) => setYearInput(e.target.value)}
                  onBlur={handleYearSubmit}
                  onKeyDown={handleYearInputKeyDown}
                  className="w-16 h-7 text-sm text-center rounded-full border border-border"
                  min="1900"
                  max={new Date().getFullYear() + 10}
                />
              ) : (
                <button
                  className="h-7 px-2 text-sm font-medium rounded-full border border-border hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all cursor-pointer"
                  onClick={() => setShowYearInput(true)}
                >
                  {currentDate.getFullYear()}
                </button>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth('next')}
              className="h-10 w-10 p-0 rounded-full border border-border hover:bg-accent hover:border-accent"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Week days header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendar()}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}